import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze } from "../util/misc.js";
import FileRef, { IMAGE_ACCEPT } from "./FileRef.js";

export const DOCUMENT_FIELDS = {
  info: {},
  type: "",
  file: null,
};
deepFreeze(DOCUMENT_FIELDS);

export const DOCUMENT_TYPES = {
  ack: {
    label: "Acknowledgement",
    domain: "vendor",
    accept: ".pdf",
  },
  aia: {
    label: "Pay Application",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  attachment: {
    label: "Attachment",
    domain: "customer",
    accept: "*",
  },
  bill: {
    label: "Bill",
    domain: "vendor",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  contract: {
    label: "Contract",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
    info: {
      totalCost: {
        label: "Contract Total",
        icon: "sum",
        type: "currency",
      },
    },
  },
  "credit-app": {
    label: "Credit Application",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "credit-reference": {
    label: "Credit Reference",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "customer-w9": {
    label: "W9",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "delivery-proof": {
    label: "Delivery Proof",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf, .mp4, video/*`,
  },
  "delivery-report": {
    label: "Delivery Report",
    domain: "customer",
    accept: ".pdf",
  },
  "expense-receipt": {
    label: "Expense Receipt",
    domain: "internal",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "intent-letter": {
    label: "Letter of Intent",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf, .txt, .doc, .docx`,
  },
  "issue-media": {
    label: "Issue Photos/Videos",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf, .mp4, video/*`,
  },
  "service-issue-media": {
    label: "Service Photos/Videos",
    domain: "customer",
    accept: ".jpg, .pdf, image/*, .mp4, video/*",
  },
  "warranty-order": {
    label: "Warranty Order Documents",
    domain: "customer",
    accept: ".jpg, .pdf, image/*",
  },
  "labor-report": {
    label: "Labor Report",
    domain: "customer",
    accept: ".pdf",
  },
  "misc-legal": {
    label: "Legal Documents",
    domain: "customer",
    accept: ".pdf",
  },
  "order-copy": {
    label: "Order Copy",
    domain: "vendor",
    accept: ".pdf",
  },
  "packing-slip": {
    label: "Packing Slip",
    domain: "vendor",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "plans-specs": {
    label: "Plans/Specs",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "project-media": {
    label: "Photos/Videos",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .mp4, video/*`,
  },
  "shop-drawings": {
    label: "Shop Drawings",
    domain: "vendor",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "purchase-order": {
    label: "Purchase Order",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
    info: {
      totalCost: {
        label: "Total Cost",
        icon: "sum",
        type: "currency",
      },
    },
  },
  "qc-report": {
    label: "Quality Control Report",
    domain: "vendor",
    accept: ".pdf",
  },
  "signed-order": {
    label: "Signed Order",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "tax-exempt-cert": {
    label: "Tax Exemption Certificate",
    domain: "customer",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "tx-receipt": {
    label: "Receipt",
    domain: "internal",
    accept: `${IMAGE_ACCEPT}, .pdf`,
  },
  "vendor-account-doc": {
    label: "Vendor Account Document",
    domain: "vendor",
    accept: `${IMAGE_ACCEPT}, .pdf`,
    info: {
      type: {
        label: "Type",
        icon: "file_type",
        type: "filter-select",
        attrs: {
          options: [
            "Certificate of Insurance",
            "Credit Application",
            "Workers Comp",
            "W9",
          ],
          valuesOnly: true,
          showAll: true,
        },
      },
      expires: {
        label: "Expires",
        icon: "due_date",
        type: "date",
        attrs: {
          future: true,
          relative: true,
          todayBtn: true,
        },
      },
    },
  },
};
deepFreeze(DOCUMENT_TYPES);

// The input for each kind of DOCUMENT_TYPES `info` field; any other `type`
// names its component directly (e.g. "filter-select").
export const INFO_FIELD_COMPONENTS = {
  boolean: "q-checkbox",
  currency: "Money",
  date: "DateInput",
  text: "q-input",
};
deepFreeze(INFO_FIELD_COMPONENTS);

// The info a document of `type` keeps: its type's fields, nothing else.
export const pickDocumentInfo = (type, info) => {
  const fields = (DOCUMENT_TYPES[type] && DOCUMENT_TYPES[type].info) || {};
  return Object.keys(fields).reduce((picked, key) => {
    if (info && info[key] !== undefined && info[key] !== null) {
      picked[key] = info[key];
    }
    return picked;
  }, {});
};

export default class DocumentRef extends DataStructure {
  constructor(data = {}) {
    // Backward compatibility: Move 'data' to 'info'
    if ("data" in data) {
      data = { ...data };
      data.info = data.data;
      delete data.data;
    }

    data = sanitize(data, DOCUMENT_FIELDS);

    super(data);

    // Initialize file data
    if (this.data.file) {
      this.data.file = this.file.data;
    }
  }

  // Editing

  get isValid() {
    return Boolean(
      this.data.file && (this.data.file.url || this.data.file.toSave)
    );
  }

  get isSaved() {
    return Boolean(
      this.data.file && this.data.file.url && !this.data.file.toSave
    );
  }

  get file() {
    return this.data.file ? new FileRef(this.data.file) : null;
  }

  set file(file) {
    if (file) {
      this.data.file = new FileRef(file).data;
    } else {
      this.data.file = null;
    }
  }

  // Formatting

  get type() {
    return DOCUMENT_TYPES[this.data.type];
  }

  get domain() {
    return this.type.domain;
  }
}
